import axios from 'axios';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.management_api.stock_management;

// Axios instance with auth interceptor (token managed automatically)
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
}));

// Cached API wrapper - 20s TTL for real-time stock data
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'management_api',
  group: 'stock_management',
  defaultTtl: '20s',
  hashLargeSegments: true,
  emitMeta: true,
});

// =====================
// MUTATIONS (no cache, invalidate after)
// =====================

// /SetSystemStock with example payload:
// {
//   "item_id": 1,
//   "storage_unit_id": 1,
//   "system_stock": 100
// }
export const setSystemStock = async (payload: any) => {
  const res = await retryWithBackoff(() => api.post('/SetSystemStock', payload));
  await cachedApi.clear();
  return res;
};

// /SetReportedStock with example payload:
// {
//   "ingredient_id": 2,
//   "storage_unit_id": 1,
//   "reported_stock": 50
// }
export const setReportedStock = async (payload: any) => {
  const res = await retryWithBackoff(() => api.post('/SetReportedStock', payload));
  await cachedApi.clear();
  return res;
};

export const setShortageSolved = async (shortage_id: number, payload: any) => {
  const res = await retryWithBackoff(() => api.put(`/SetShortageSolved/${shortage_id}`, payload));
  await cachedApi.clear();
  return res;
};

export const updateIngredientStock = async (ingredientId: number, storage_id: number, stock: number) => {
  const payload = { reported_stock: stock };
  const res = await retryWithBackoff(() => api.put(`/UpdateIngredientStock/${ingredientId}/${storage_id}`, payload));
  await cachedApi.clear();
  return res;
};

export const transferIngredientStock = async (ingredientId: number, payload: any) => {
  const res = await retryWithBackoff(() => api.put(`/TransferIngredientStock/${ingredientId}`, payload));
  await cachedApi.clear();
  return res;
};

export const transferProductStock = async (productID: string, payload: any) => {
  const res = await retryWithBackoff(() => api.put(`/TransferProductStock/${productID}`, payload));
  await cachedApi.clear();
  return res;
};

export const transferUtilityStock = async (utilityId: number, payload: any) => {
  const res = await retryWithBackoff(() => api.put(`/TransferUtilityStock/${utilityId}`, payload));
  await cachedApi.clear();
  return res;
};

export const createIngredientStock = async (data: any) => {
  const res = await retryWithBackoff(() => api.post('/CreateIngredientStock', data));
  await cachedApi.clear();
  return res;
};

export const createUtilityStock = async (data: any) => {
  const res = await retryWithBackoff(() => api.post('/CreateUtilityStock', data));
  await cachedApi.clear();
  return res;
};

export const createProductStock = async (data: any) => {
  const res = await retryWithBackoff(() => api.post('/CreateProductStock', data));
  await cachedApi.clear();
  return res;
};

// /DeleteProductStock/<string:product_id>/<int:storage_unit_id> 
export const deleteProductStock = async (productID: string, storage_id: number) => {
  const res = await retryWithBackoff(() => api.delete(`/DeleteProductStock/${productID}/${storage_id}`));
  await cachedApi.clear();
  return res;
};

export const updateMinimumStock = async (ingredientId: number, storageUnitId: number, minQuantity: number) => {
  const payload = { ingredient_id: ingredientId, storage_unit_id: storageUnitId, min_quantity: minQuantity };
  const res = await retryWithBackoff(() => api.post('/UpdateMinimumStock', payload));
  await cachedApi.clear();
  return res;
};

export const equalProductStock = async (ingredientId: number, storageUnitId: number, makeSystem: boolean) => {
  const payload = { ingredient_id: ingredientId, storage_unit_id: storageUnitId, makeSystem: makeSystem };
  const res = await retryWithBackoff(() => api.post('/EqualProductStock', payload));
  await cachedApi.clear();
  return res;
};

export const toggleStockFavorite = async (ingredientId: number, storageUnitId: number) => {
  const res = await retryWithBackoff(() => api.put(`/ToggleStockFavorite/${storageUnitId}/${ingredientId}`));
  await cachedApi.clear();
  return res;
};

export const toggleStockFollowing = async (ingredientId: number, storageUnitId: number) => {
  const res = await retryWithBackoff(() => api.put(`/ToggleStockFollowing/${storageUnitId}/${ingredientId}`));
  await cachedApi.clear();
  return res;
};

export const updateSensitivity = async (ingredientId: number, storageUnitId: number, sensitivity: number) => {
  const payload = { sensitivity: sensitivity };
  const res = await retryWithBackoff(() => api.put(`/UpdateSensitivity/${storageUnitId}/${ingredientId}`, payload));
  await cachedApi.clear();
  return res;
};

// =====================
// READS (cached with 20s TTL)
// =====================

// /GetStockByCompanyId/<int:company_id>
export const getStockByCompanyId = async (companyId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetStockByCompanyId/${companyId}`));
};

// /GetStockByLocalId/<int:local_id>
export const getStockByLocalId = async (localId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetStockByLocalId/${localId}`));
};

export const getProductStockByLocalId = async (localId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetProductStockByLocalId/${localId}`));
};

export const getProductStockByIdAndStorage = async (ingredientId: number, storageUnitId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetProductStockByIdAndStorage/${ingredientId}/${storageUnitId}`));
};

//GetAllStockShortagesOfCurrentSessionByLocalId
export const getAllStockShortagesOfCurrentSessionByLocalId = async (localId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetAllStockShortagesOfCurrentSessionByLocalId/${localId}`));
};

//GetSessionStockSummaryByLocalId
export const getSessionStockSummaryByLocalId = async (localId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetSessionStockSummaryByLocalId/${localId}`));
};

// /GetStockByStorageUnitId/<int:storage_unit_id>
export const getStockByStorageUnitId = async (storageUnitId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetStockByStorageUnitId/${storageUnitId}`));
};

// /GetStorageUnitMovementsByCompanyId/<int:company_id>
export const getStorageUnitMovementsByCompanyId = async (companyId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetStorageUnitMovementsByCompanyId/${companyId}`));
};

// /GetStorageUnitMovementsByLocalId/<int:local_id>
export const getStorageUnitMovementsByLocalId = async (localId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetStorageUnitMovementsByLocalId/${localId}`));
};

// /GetStorageUnitMovementsByStorageUnitId/<int:storage_unit_id>
export const getStorageUnitMovementsByStorageUnitId = async (storageUnitId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetStorageUnitMovementsByStorageUnitId/${storageUnitId}`));
};

export const getProductsForStockByLocal = async (localId: number) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetProductsForStockByLocal/${localId}`));
};

export const getProductFullStockById = async (productId: string) => {
  return await retryWithBackoff(() => cachedApi.get(`/GetProductFullStockById/${productId}`));
};

// =====================
// QUERY ENDPOINTS (POST but semantically reads - cached)
// =====================

/** 
 * Get all stock shortages by local ID and date range
 */
export const getAllStockShortagesByLocalIdAndDateRange = async (
  localId: number, 
  startDate: string, 
  endDate: string,
  ingredientId?: number | undefined,
  storageUnitId?: number | undefined  
) => {
  return await retryWithBackoff(() => cachedApi.post(`/GetAllStockShortagesByLocalIdAndDateRange/${localId}`, {
    start_date: startDate,
    end_date: endDate,
    ingredient_id: ingredientId,
    storage_unit_id: storageUnitId
  }));
};

export const getAllStockShortagesByIngredientId = async (ingredientId: number, startDate: string, endDate: string) => {
  return await retryWithBackoff(() => cachedApi.post(`/GetAllStockShortagesByIngredientId/${ingredientId}`, {
    start_date: startDate,
    end_date: endDate
  }));
};

/**
 * Get all mermas by local ID and date range
 */
export const getAllMermasByLocalId = async (localId: number, startDate: string, endDate: string, ingredientId?: number) => {
  const payload: any = {
    start_date: startDate,
    end_date: endDate
  };
  if (ingredientId !== undefined) {
    payload.ingredient_id = ingredientId;
  }
  return await retryWithBackoff(() => cachedApi.post(`/GetAllMermasByLocalId/${localId}`, payload));
};

export const getAllMermasByIngredientId = async (ingredientId: number, startDate: string, endDate: string) => {
  return await retryWithBackoff(() => cachedApi.post(`/GetAllMermasByIngredientId/${ingredientId}`, {
    start_date: startDate,
    end_date: endDate
  }));
};